import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Icon as LegacyIcon } from 'legacy-packages/ft-cb';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import Tooltip from 'chameleon/ui-stack/ui/base/tooltip';
import { addQuery, buildUrl } from 'legacy-src/utility/buildUrl';
import findLast from 'lodash/findLast';
import PerformanceBarPopover, {
  questionFRQTiers,
  questionTiers,
} from 'legacy/src/components/assessment/Results/PerformanceBarPopover/index';
import {
  performanceTierStacks,
  isFRQ,
  getScoreTimeSpents,
  averageTimeSpentInSeconds,
  secondsToTime,
} from 'legacy-src/utility/progressHelpers';
import { SecureInfoIcon, APTopicQuestionIcon } from 'legacy-src/utility';
import IconWithDescription from 'legacy-src/utility/IconWithDescription';

import viewForPath from '../../../content/view/viewForPath';

import PerformanceIcon, { Performance } from '../../PerformanceIcon';
import './ResultsItemHeader.scss';
// eslint-disable-next-line import/no-extraneous-dependencies
import uniqid from 'uniqid';
import { getPriorStudentsSubdir } from 'legacy-src/utility/utils';
import PracticeSkillsPopOver from 'chameleon-packages/units/components/PracticeSkills/PracticeSkillsPopOver';
import TeacherRemovedQuestionIcon from 'legacy-src/utility/IconWithDescription/TeacherRemovedQuestionIcon';
import RetiredItemIcon from 'legacy-src/utility/IconWithDescription/RetiredItemIcon';
import { getSkillsFromRelatedSkills } from '../utility';

const ResultsItemHeader = (props) => {
  const {
    location,
    params,
    routes,
    item,
    isSPP,
    result,
    prevQuestionId,
    nextQuestionId,
    prevPartId,
    nextPartId,
    disableLinks,
    students,
    studentUrl,
    learnosityReportsByItemData,
    learnosityReportsByItemDataFull,
    learnosityReportScoreParsed,
    learnosityReportMaxScore,
    isRubricItem,
    isYourResult,
    useQuestionNumberAsTitle,
    showQuestionNumbers,
    totalQuestions,
    currentQuestionNumber,
    currentQuestionIndex,
    hideChart,
    showSecureIcon,
    showTopicQuestionIcon,
    customTitle,
    displaySkillsFlag,
    showResults,
    me,
    studentsSeeTeacherCreatedFF,
    retireQbQuestionsFF,
    pacingFlag,
    router,
  } = props;
  const { query } = location;
  const lastRouteName = findLast(routes, 'name');
  const lastRoute = lastRouteName ? lastRouteName.name : {};
  const { partId } = params;
  const part =
    partId && (item.children || []).find((rubric) => rubric.id === partId);
  const partTitle = part && part.title;
  const itemTitle = customTitle || item.title;
  const { itemShareChain } = item || {};
  const showRetiredQuestionIcon =
    item.isRetired && !item.isTeacherAuthored && !me.isStudent;
  const showTeacherRemovedQuestionIcon =
    item.isRetired &&
    item.isTeacherAuthored &&
    !me.isStudent &&
    retireQbQuestionsFF;

  const titleContent = useQuestionNumberAsTitle
    ? `Question ${currentQuestionNumber}`
    : partTitle
      ? `${itemTitle}: ${partTitle}`
      : `${itemTitle}`;

  const avgTimeSpent = () => {
    const { averageTimeSpents } = getScoreTimeSpents(
      learnosityReportsByItemDataFull,
    );
    return secondsToTime(
      averageTimeSpentInSeconds(
        averageTimeSpents[item.learnosityItemReference],
      ),
    );
  };

  const renderIcon = () => {
    let iconName;
    let popOverText;
    if (item.isTeacherAuthored === true && !itemShareChain && !me.isStudent) {
      popOverText = 'You created this question.';
      iconName = 'my_question';
    } else if (
      item.isTeacherAuthored === true &&
      itemShareChain &&
      !me.isStudent
    ) {
      popOverText = 'Another educator created this item.';
      iconName = 'my_copied_question';
    } else if (
      item.isTeacherAuthored &&
      me.isStudent &&
      studentsSeeTeacherCreatedFF
    ) {
      popOverText = 'This question was created by an educator.';
      iconName = 'my_copied_question';
    } else if (!item.isTeacherAuthored || !studentsSeeTeacherCreatedFF) {
      return;
    }
    if (iconName && popOverText) {
      return (
        <div>
          <IconWithDescription
            buttonClassName="flex items-start"
            className="ml-4 cursor-pointer"
            trigger={
              <Icon
                data-test-id="author_info_icon"
                name={iconName}
                width="20"
                height="20"
              />
            }
          >
            {popOverText}
          </IconWithDescription>
        </div>
      );
    }
  };

  const title =
    disableLinks === true ? (
      <React.Fragment>
        <h2 className="RI_header__title RI_header__title__nolin gap-4">
          {titleContent}{' '}
          {showSecureIcon && <SecureInfoIcon showFullLabel={isYourResult} />}
          {showTopicQuestionIcon && <APTopicQuestionIcon />}
          {renderIcon()}
          {showRetiredQuestionIcon && <RetiredItemIcon isQuestion />}
          {showTeacherRemovedQuestionIcon && <TeacherRemovedQuestionIcon />}
        </h2>
        {pacingFlag && !isSPP && avgTimeSpent() && (
          <span className="flex items-center">
            {' '}
            <Tooltip
              description={isYourResult ? 'Time Spent' : `Average Time Spent`}
            >
              <Icon name="timer" width="25" height="25" />
            </Tooltip>{' '}
            <span className="ml-5 mr-2">{avgTimeSpent()}</span>
            {item.type === 'FreeResponseQuestion' ? 'for all parts' : ''}
          </span>
        )}
      </React.Fragment>
    ) : (
      <Link
        to={buildUrl(
          routes,
          lastRoute,
          params,
          addQuery(query, {
            review: 1,
            ...viewForPath(result.resultPath.split('.')),
          }),
        )}
        className="RI_header__title"
      >
        {titleContent}{' '}
        {showSecureIcon && <SecureInfoIcon showFullLabel={isYourResult} />}
      </Link>
    );

  const navArrow = (direction, index, partIndex) => {
    let itemPath;
    // No app if printing.

    const resultsPath = `${params.subject}/assessments/results/${
      params.assignmentId
    }${getPriorStudentsSubdir(routes)}`;

    const currentRoute = routes[routes.length - 1] || {};
    const { name: routeName } = currentRoute;
    const { ppcPartId } = params;

    if (
      (routeName === 'SingleStudentPerformanceItem' && !isYourResult) ||
      (routeName === 'SingleStudentPerformanceItemOption' && !isYourResult)
    ) {
      itemPath = `/${resultsPath}/students/${params.studentId}`;

      if (ppcPartId) {
        itemPath += `/parts/${ppcPartId}/performance/${index}`;
      } else {
        itemPath += `/questions/${index}`;
      }
    } else if (
      (routeName === 'SingleStudentPerformanceItem' && isYourResult) ||
      (routeName === 'SingleStudentPerformanceItemPart' && isYourResult)
    ) {
      itemPath = `/${resultsPath}/performance/${index}`;
    } else {
      itemPath = `/${resultsPath}/questions/${index}`;
    }
    if (partIndex) {
      itemPath = `${itemPath}/parts/${partIndex}`;
    }

    if (index !== null) {
      return (
        <Link
          to={{
            pathname: itemPath,
            state: {
              ...(router?.location?.state || {}),
            },
          }}
          className="RI_header__button"
          aria-label={`${direction === 'left' ? 'Previous' : 'Next'} response`}
        >
          <LegacyIcon name={`chevron_${direction}`} size="1em" />
        </Link>
      );
    }
    return (
      <span className="RI_header__button disabled">
        <LegacyIcon name={`chevron_${direction}`} size="1em" />
      </span>
    );
  };

  const isFRQItem = isFRQ(item);
  const tiers = isFRQItem ? questionFRQTiers : questionTiers;
  const learnosityItemReference = part
    ? part.learnosityItemReference
    : item.learnosityItemReference;

  let getPerformanceStacks;
  let stacks;
  let performanceBarPopover;

  if (!hideChart) {
    getPerformanceStacks = (itemId) =>
      performanceTierStacks(
        students && students.length,
        learnosityReportsByItemData,
        tiers,
        students,
        itemId,
      );
    // TODO: Remove when ready
    stacks =
      students && students.length
        ? getPerformanceStacks(learnosityItemReference)
        : [];

    if (stacks.length) {
      const ariaLabel = `Open student results for question: ${stacks.reduce(
        (a, b) => `${a}, ${b.label}: ${b.value}`,
        item.title,
      )}`;

      const popOverContentId = `question-performance-popover-${uniqid()}`;
      const studentRedirectionUrl =
        studentUrl ||
        `/${params.subject}/assessments/results/${params.assignmentId}/students`;

      performanceBarPopover = (
        <PerformanceBarPopover
          data-test-perf-bar
          isWeighed
          showScore={false}
          stacks={stacks}
          ariaLabel={ariaLabel}
          ariaDescribedBy={popOverContentId}
          contentOffset="0 -2rem 0 0"
          studentUrl={studentRedirectionUrl}
        />
      );
    }
  }

  const performance = learnosityReportScoreParsed
    ? learnosityReportScoreParsed / learnosityReportMaxScore === 1
      ? Performance.Max
      : Performance.Partial
    : Performance.Zero;
  const performanceIconlabel = isFRQItem
    ? ' FRQ points'
    : !isRubricItem
      ? ' MC point'
      : '';

  return (
    <header className="RI_header">
      {title}
      <div className="RI_header__skills">
        {displaySkillsFlag && (
          <PracticeSkillsPopOver
            skillsList={getSkillsFromRelatedSkills(item.relatedSkills)}
          />
        )}
      </div>
      {showResults &&
        (isYourResult ? (
          <Fragment>
            {learnosityReportMaxScore ? (
              <PerformanceIcon
                score={learnosityReportScoreParsed}
                maxScore={learnosityReportMaxScore}
                size="big"
                performance={performance}
                label={performanceIconlabel}
              />
            ) : (
              <span data-test-no-results>No result</span>
            )}
          </Fragment>
        ) : (
          !!performanceBarPopover && (
            <div className="RI_header__stacked_bar">
              {performanceBarPopover}
            </div>
          )
        ))}
      <div className="RI_header__performance_box">
        {navArrow('left', prevQuestionId, prevPartId)}
        {showQuestionNumbers && (
          <span className="RI_header__question_counters">
            {currentQuestionIndex + 1} of {totalQuestions}
          </span>
        )}
        {navArrow('right', nextQuestionId, nextPartId)}
      </div>
    </header>
  );
};

ResultsItemHeader.propTypes = {
  item: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  routes: PropTypes.array.isRequired,
  useQuestionNumberAsTitle: PropTypes.bool,
  showQuestionNumbers: PropTypes.bool,
  currentQuestionNumber: PropTypes.number,
  currentQuestionIndex: PropTypes.number,
  totalQuestions: PropTypes.number,
  hideChart: PropTypes.bool,
  showResults: PropTypes.bool,
  me: PropTypes.object.isRequired,
  studentsSeeTeacherCreatedFF: PropTypes.bool,
  retireQbQuestionsFF: PropTypes.bool,
  pacingFlag: PropTypes.bool,
};

ResultsItemHeader.defaultProps = {
  students: [],
  useQuestionNumberAsTitle: false,
  showQuestionNumbers: false,
  // the number of the question in the scheme of the full assessment.
  currentQuestionNumber: 1,
  // the index of the question in the set of total questions currently being viewed.
  currentQuestionIndex: 0,
  totalQuestions: 1,
  hideChart: false,
  showResults: true,
  retireQbQuestionsFF: false,
  pacingFlag: false,
};

export default ResultsItemHeader;
